import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { TradingSocket } from '../../../services/TradingSocket';
import { tradingService } from '../../../services/TradingService';
import {
  BlotterStatusUpdate,
  IntradaySignalBatchUpdate,
  IntradayStrategyStartRequest,
  OrderEvent,
  OptionContractLookupRow,
  OrderPreview,
  OrderTicketPrefill,
  PlaceOrderRequest,
  PositionStateSnapshot,
  ScheduleStrategyRequest,
  ScheduleStrategyResponse,
  SystemAlert,
  TickL1,
  TradingOrder,
  TradingOrderBlotterRow,
  TradingWsEnvelope,
} from '../../../types/trading';
import { OrderTicketNavigationMenu } from './OrderTicketNavigationMenu';
import type { OrderTicketPaneMode } from './OrderTicketNavigationMenu';
import type { StrategyTicketContext } from '../strategy-backtest/types/context';
import {
  buildOrderTicketPaneModeStorageKey,
  buildOrderTicketPersistenceScope,
  migrateLegacyDashboardOrderTicketStorage,
  readSessionStorageJson,
  writeSessionStorageJson,
} from '../persistence/orderTicketPersistence';

type OrderEventHandler = (event: OrderEvent) => void;
type PortfolioStateUpdateHandler = (positionState: PositionStateSnapshot) => void;
type SystemAlertHandler = (alert: SystemAlert) => void;
type RiskRejectHandler = (payload: unknown) => void;
type IntradaySignalHandler = (update: IntradaySignalBatchUpdate) => void;
type BlotterRowUpsertHandler = (row: TradingOrderBlotterRow) => void;
type BlotterStatusUpdateHandler = (update: BlotterStatusUpdate) => void;

interface OpenOrderTicketOptions {
  prefill?: OrderTicketPrefill | null;
}

interface OpenStrategyOrderTicketOptions {
  context?: StrategyTicketContext;
  forceOpen?: boolean;
}

interface OrderTicketNavigationContextType {
  wsConnected: boolean;
  ticksBySymbol: Record<string, TickL1>;
  isSimulationEnabled: boolean | null;
  retainSymbolSubscription: (symbol: string) => void;
  releaseSymbolSubscription: (symbol: string) => void;
  subscribeToOrderEvents: (handler: OrderEventHandler) => () => void;
  subscribeToPortfolioStateUpdates: (handler: PortfolioStateUpdateHandler) => () => void;
  subscribeToSystemAlerts: (handler: SystemAlertHandler) => () => void;
  subscribeToRiskRejects: (handler: RiskRejectHandler) => () => void;
  subscribeToIntradaySignalUpdates: (handler: IntradaySignalHandler) => () => void;
  subscribeToBlotterRowUpserts: (handler: BlotterRowUpsertHandler) => () => void;
  subscribeToBlotterStatusUpdates: (handler: BlotterStatusUpdateHandler) => () => void;
  startIntradaySignalTest: (strategies: IntradayStrategyStartRequest[]) => void;
  takeOverIntradaySignalTest: (strategies: IntradayStrategyStartRequest[]) => void;
  stopIntradaySignalTest: (strategyId?: string) => void;
  openOrderTicket: (options?: OpenOrderTicketOptions) => void;
  closeOrderTicket: () => void;
  isOrderTicketOpen: boolean;
  openStrategyOrderTicket: (options?: OpenStrategyOrderTicketOptions) => void;
  closeStrategyOrderTicket: () => void;
  isStrategyOrderTicketOpen: boolean;
  previewOrder: (request: PlaceOrderRequest) => Promise<OrderPreview>;
  placeOrder: (request: PlaceOrderRequest) => Promise<TradingOrder>;
  scheduleStrategy: (request: ScheduleStrategyRequest) => Promise<ScheduleStrategyResponse>;
}

const OrderTicketNavigationContext = createContext<OrderTicketNavigationContextType | undefined>(undefined);

const CASH_SYMBOL = 'CASH';

const normalizeSymbol = (symbol: string | null | undefined): string => {
  return symbol?.trim().toUpperCase() ?? '';
};

const isOrderTicketPaneMode = (value: unknown): value is OrderTicketPaneMode =>
  value === 'manual' || value === 'strategy';

const isActiveTradingRoute = (pathname: string): boolean => {
  if (pathname === '/trading' || pathname === '/dashboard' || pathname === '/chat') return true;
  if (
    pathname === '/' ||
    pathname === '/home' ||
    pathname === '/about' ||
    pathname === '/privacy' ||
    pathname === '/terms'
  ) {
    return false;
  }

  // Remaining app routes currently map to chat session paths (/:chatHash).
  return true;
};

const resolveStrategyTicketContext = (pathname: string): StrategyTicketContext => {
  if (pathname === '/trading' || pathname === '/dashboard') return 'trading';
  return 'chat';
};

const asSystemAlert = (value: unknown): SystemAlert | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const level = typeof raw.level === 'string' ? raw.level : 'info';
  const message = typeof raw.message === 'string' ? raw.message : '';
  const timestamp = typeof raw.timestamp === 'string' ? raw.timestamp : new Date().toISOString();
  if (!message) return null;
  return { level, message, timestamp };
};

const emit = <T,>(handlers: Set<(payload: T) => void>, payload: T): void => {
  handlers.forEach((handler) => {
    try {
      handler(payload);
    } catch {
      // Keep other listeners alive if one handler fails.
    }
  });
};

interface OrderTicketNavigationProviderProps {
  children: ReactNode;
}

export const OrderTicketNavigationProvider: React.FC<OrderTicketNavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, userUuid, getToken } = useAuth();
  const routeStrategyContext = useMemo(
    () => resolveStrategyTicketContext(location.pathname),
    [location.pathname],
  );
  const orderTicketPersistenceScope = useMemo(
    () => buildOrderTicketPersistenceScope(location.pathname, routeStrategyContext),
    [location.pathname, routeStrategyContext],
  );
  const paneModeStorageKey = useMemo(
    () => buildOrderTicketPaneModeStorageKey(orderTicketPersistenceScope),
    [orderTicketPersistenceScope],
  );

  const socketRef = useRef<TradingSocket | null>(null);
  const symbolSubscriptionCountsRef = useRef<Map<string, number>>(new Map());
  const orderHandlersRef = useRef<Set<OrderEventHandler>>(new Set());
  const portfolioStateHandlersRef = useRef<Set<PortfolioStateUpdateHandler>>(new Set());
  const alertHandlersRef = useRef<Set<SystemAlertHandler>>(new Set());
  const riskRejectHandlersRef = useRef<Set<RiskRejectHandler>>(new Set());
  const intradaySignalHandlersRef = useRef<Set<IntradaySignalHandler>>(new Set());
  const blotterRowUpsertHandlersRef = useRef<Set<BlotterRowUpsertHandler>>(new Set());
  const blotterStatusUpdateHandlersRef = useRef<Set<BlotterStatusUpdateHandler>>(new Set());

  const [wsConnected, setWsConnected] = useState(false);
  const [ticksBySymbol, setTicksBySymbol] = useState<Record<string, TickL1>>({});
  const [isOrderTicketPaneOpen, setIsOrderTicketPaneOpen] = useState(false);
  const [orderTicketPaneMode, setOrderTicketPaneMode] = useState<OrderTicketPaneMode>('manual');
  const [paneModeHydrationKey, setPaneModeHydrationKey] = useState<string | null>(null);
  const [orderTicketPrefill, setOrderTicketPrefill] = useState<OrderTicketPrefill | null>(null);
  const [strategyOrderTicketContext, setStrategyOrderTicketContext] = useState<StrategyTicketContext>(() =>
    resolveStrategyTicketContext(location.pathname)
  );
  const [isStrategyTicketLocked, setIsStrategyTicketLocked] = useState(false);
  const [pendingStrategyContext, setPendingStrategyContext] = useState<StrategyTicketContext | null>(null);
  const [isSimulationEnabled, setIsSimulationEnabled] = useState<boolean | null>(null);
  const isOrderTicketOpen = isOrderTicketPaneOpen && orderTicketPaneMode === 'manual';
  const isStrategyOrderTicketOpen = isOrderTicketPaneOpen && orderTicketPaneMode === 'strategy';

  useEffect(() => {
    migrateLegacyDashboardOrderTicketStorage();
  }, []);

  const shouldRunTradingSocket = isAuthenticated && Boolean(userUuid) && isActiveTradingRoute(location.pathname);
  const tradingWsMarketStream = location.pathname === '/intradaysignaltest' ? 'raw' : undefined;

  const retainSymbolSubscription = useCallback((rawSymbol: string) => {
    const symbol = normalizeSymbol(rawSymbol);
    if (!symbol || symbol === CASH_SYMBOL) return;

    const counts = symbolSubscriptionCountsRef.current;
    const current = counts.get(symbol) ?? 0;
    counts.set(symbol, current + 1);

    if (current === 0) {
      socketRef.current?.subscribe(symbol);
    }
  }, []);

  const releaseSymbolSubscription = useCallback((rawSymbol: string) => {
    const symbol = normalizeSymbol(rawSymbol);
    if (!symbol || symbol === CASH_SYMBOL) return;

    const counts = symbolSubscriptionCountsRef.current;
    const current = counts.get(symbol) ?? 0;
    if (current <= 1) {
      counts.delete(symbol);
      socketRef.current?.unsubscribe(symbol);
      return;
    }

    counts.set(symbol, current - 1);
  }, []);

  useEffect(() => {
    if (!shouldRunTradingSocket || !userUuid) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setWsConnected(false);
      return;
    }

    const socket = new TradingSocket();
    socketRef.current = socket;
    const subscriptionCounts = symbolSubscriptionCountsRef.current;

    const currentSymbols = Array.from(subscriptionCounts.entries())
      .filter(([, count]) => count > 0)
      .map(([symbol]) => symbol);

    socket.connect(
      userUuid,
      currentSymbols,
      (event: TradingWsEnvelope<any>) => {
        if (event.type === 'tick_l1') {
          const tick = event.data as TickL1;
          const symbol = normalizeSymbol(tick.symbol);
          if (!symbol) return;
          setTicksBySymbol((prev) => ({ ...prev, [symbol]: { ...tick, symbol } }));
          return;
        }

        if (event.type === 'order_event') {
          emit(orderHandlersRef.current, event.data as OrderEvent);
          return;
        }

        if (event.type === 'portfolio_state_update') {
          const incoming = event.data as PositionStateSnapshot;
          const normalizedNormalPositions: Record<string, PositionStateSnapshot['normal_positions'][string]> = {};
          Object.entries(incoming.normal_positions || {}).forEach(([key, position]) => {
            const normalizedSymbol = normalizeSymbol(position.symbol || '') || position.symbol || key;
            normalizedNormalPositions[key] = {
              ...position,
              lot_id: position.lot_id || key,
              closed_qty: Number.isFinite(position.closed_qty) ? Math.max(0, position.closed_qty) : 0,
              symbol: normalizedSymbol,
            };
          });

          emit(portfolioStateHandlersRef.current, {
            ...incoming,
            normal_positions: normalizedNormalPositions,
          });
          return;
        }

        if (event.type === 'system_alert') {
          const alert = asSystemAlert(event.data);
          if (alert) {
            emit(alertHandlersRef.current, alert);
          }
          return;
        }

        if (event.type === 'risk_reject') {
          emit(riskRejectHandlersRef.current, event.data);
          return;
        }

        if (event.type === 'intraday_signal_update') {
          emit(intradaySignalHandlersRef.current, event.data as IntradaySignalBatchUpdate);
          return;
        }

        if (event.type === 'blotter_status_update') {
          emit(blotterStatusUpdateHandlersRef.current, event.data as BlotterStatusUpdate);
          return;
        }

        if (event.type === 'subscription_result') {
          return;
        }
      },
      (connected) => {
        setWsConnected(connected);
      },
      tradingWsMarketStream ? { marketStream: tradingWsMarketStream } : undefined,
    );

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      setWsConnected(false);
    };
  }, [shouldRunTradingSocket, tradingWsMarketStream, userUuid]);

  const subscribeToOrderEvents = useCallback((handler: OrderEventHandler) => {
    orderHandlersRef.current.add(handler);
    return () => {
      orderHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToPortfolioStateUpdates = useCallback((handler: PortfolioStateUpdateHandler) => {
    portfolioStateHandlersRef.current.add(handler);
    return () => {
      portfolioStateHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToSystemAlerts = useCallback((handler: SystemAlertHandler) => {
    alertHandlersRef.current.add(handler);
    return () => {
      alertHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToRiskRejects = useCallback((handler: RiskRejectHandler) => {
    riskRejectHandlersRef.current.add(handler);
    return () => {
      riskRejectHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToIntradaySignalUpdates = useCallback((handler: IntradaySignalHandler) => {
    intradaySignalHandlersRef.current.add(handler);
    return () => {
      intradaySignalHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToBlotterRowUpserts = useCallback((handler: BlotterRowUpsertHandler) => {
    blotterRowUpsertHandlersRef.current.add(handler);
    return () => {
      blotterRowUpsertHandlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToBlotterStatusUpdates = useCallback((handler: BlotterStatusUpdateHandler) => {
    blotterStatusUpdateHandlersRef.current.add(handler);
    return () => {
      blotterStatusUpdateHandlersRef.current.delete(handler);
    };
  }, []);

  const emitBlotterRowUpsert = useCallback((row: TradingOrderBlotterRow) => {
    emit(blotterRowUpsertHandlersRef.current, row);
  }, []);

  const asBlotterStatusFromOrder = (status: TradingOrder['status']): TradingOrderBlotterRow['status'] => {
    if (status === 'pending') return 'pending';
    if (status === 'filled') return 'filled';
    return 'cancelled';
  };

  const startIntradaySignalTest = useCallback((strategies: IntradayStrategyStartRequest[]) => {
    socketRef.current?.startIntradaySignalTest(strategies);
  }, []);

  const takeOverIntradaySignalTest = useCallback((strategies: IntradayStrategyStartRequest[]) => {
    socketRef.current?.takeOverIntradaySignalTest(strategies);
  }, []);

  const stopIntradaySignalTest = useCallback((strategyId?: string) => {
    socketRef.current?.stopIntradaySignalTest(strategyId);
  }, []);

  const closeOrderTicket = useCallback(() => {
    setIsOrderTicketPaneOpen(false);
    setOrderTicketPrefill(null);
  }, []);

  const openOrderTicket = useCallback((options?: OpenOrderTicketOptions) => {
    setOrderTicketPrefill(options?.prefill ? { ...options.prefill } : null);
    setOrderTicketPaneMode('manual');
    setIsOrderTicketPaneOpen(true);
  }, []);

  const closeStrategyOrderTicket = useCallback(() => {
    setIsOrderTicketPaneOpen(false);
  }, []);

  const openStrategyOrderTicket = useCallback(
    (options?: OpenStrategyOrderTicketOptions) => {
      const nextContext = options?.context ?? resolveStrategyTicketContext(location.pathname);
      const shouldForceOpen = options?.forceOpen ?? false;

      if (isStrategyTicketLocked && !shouldForceOpen) {
        setPendingStrategyContext(nextContext);
        return;
      }

      setPendingStrategyContext(null);
      setStrategyOrderTicketContext(nextContext);
      setOrderTicketPaneMode('strategy');
      setIsOrderTicketPaneOpen(true);
    },
    [isStrategyTicketLocked, location.pathname]
  );

  const handlePaneModeChange = useCallback((mode: OrderTicketPaneMode) => {
    if (mode === 'manual') {
      setOrderTicketPaneMode('manual');
      return;
    }

    const nextContext = resolveStrategyTicketContext(location.pathname);
    if (isStrategyTicketLocked && orderTicketPaneMode !== 'strategy') {
      setPendingStrategyContext(nextContext);
      return;
    }

    setStrategyOrderTicketContext(nextContext);
    setOrderTicketPaneMode('strategy');
  }, [isStrategyTicketLocked, location.pathname, orderTicketPaneMode]);

  useEffect(() => {
    let canceled = false;

    if (!isAuthenticated || !userUuid) {
      setIsSimulationEnabled(null);
      return () => {
        canceled = true;
      };
    }

    const loadRiskLimits = async () => {
      try {
        const token = await getToken();
        const riskLimits = await tradingService.getRiskLimits(userUuid, token || undefined);
        if (!canceled) {
          setIsSimulationEnabled(riskLimits.simulation_enabled === true);
        }
      } catch {
        if (!canceled) {
          setIsSimulationEnabled(null);
        }
      }
    };

    void loadRiskLimits();

    return () => {
      canceled = true;
    };
  }, [getToken, isAuthenticated, userUuid]);

  useEffect(() => {
    const handleStreamingStateChange = (event: CustomEvent<{ isStreaming: boolean }>) => {
      setIsStrategyTicketLocked(event.detail?.isStreaming ?? false);
    };

    window.addEventListener('streamingStateChange', handleStreamingStateChange as EventListener);
    return () => {
      window.removeEventListener('streamingStateChange', handleStreamingStateChange as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isStrategyTicketLocked && pendingStrategyContext) {
      setStrategyOrderTicketContext(pendingStrategyContext);
      setOrderTicketPaneMode('strategy');
      setIsOrderTicketPaneOpen(true);
      setPendingStrategyContext(null);
    }
  }, [isStrategyTicketLocked, pendingStrategyContext]);

  // Compatibility bridge for existing AI chat flow events.
  useEffect(() => {
    const handleOpenManualOrderTicket = (event: CustomEvent<{ prefill?: OrderTicketPrefill | null }>) => {
      openOrderTicket({ prefill: event.detail?.prefill ?? null });
    };

    const handleOpenSideConfig = (event: CustomEvent<{ tab?: string; context?: StrategyTicketContext }>) => {
      const tab = event.detail?.tab;
      if (tab && tab !== 'backtest') return;

      const context = event.detail?.context ?? resolveStrategyTicketContext(location.pathname);
      const shouldForceOpen = tab === 'backtest';
      openStrategyOrderTicket({ context, forceOpen: shouldForceOpen });
    };

    const handleCloseSideConfig = () => {
      closeStrategyOrderTicket();
    };

    window.addEventListener('openManualOrderTicket', handleOpenManualOrderTicket as EventListener);
    window.addEventListener('openSideConfigMenu', handleOpenSideConfig as EventListener);
    window.addEventListener('closeSideConfigMenu', handleCloseSideConfig as EventListener);
    return () => {
      window.removeEventListener('openManualOrderTicket', handleOpenManualOrderTicket as EventListener);
      window.removeEventListener('openSideConfigMenu', handleOpenSideConfig as EventListener);
      window.removeEventListener('closeSideConfigMenu', handleCloseSideConfig as EventListener);
    };
  }, [closeStrategyOrderTicket, location.pathname, openOrderTicket, openStrategyOrderTicket]);

  useEffect(() => {
    if (paneModeHydrationKey !== paneModeStorageKey) return;
    writeSessionStorageJson(paneModeStorageKey, orderTicketPaneMode);
  }, [paneModeHydrationKey, paneModeStorageKey, orderTicketPaneMode]);

  useEffect(() => {
    const persistedPaneMode = readSessionStorageJson<unknown>(paneModeStorageKey);
    const nextPaneMode: OrderTicketPaneMode = isOrderTicketPaneMode(persistedPaneMode)
      ? persistedPaneMode
      : 'manual';

    setIsOrderTicketPaneOpen(false);
    setOrderTicketPaneMode(nextPaneMode);
    setOrderTicketPrefill(null);
    setPendingStrategyContext(null);
    setStrategyOrderTicketContext(routeStrategyContext);
    setPaneModeHydrationKey(paneModeStorageKey);
  }, [location.pathname, location.search, paneModeStorageKey, routeStrategyContext]);

  const previewOrder = useCallback(async (request: PlaceOrderRequest): Promise<OrderPreview> => {
    if (!userUuid) throw new Error('Missing user id');
    const token = await getToken();
    return tradingService.previewOrder(userUuid, request, token || undefined);
  }, [getToken, userUuid]);

  const placeOrder = useCallback(async (request: PlaceOrderRequest): Promise<TradingOrder> => {
    if (!userUuid) throw new Error('Missing user id');
    const token = await getToken();
    const order = await tradingService.placeOrder(userUuid, request, token || undefined);
    emitBlotterRowUpsert({
      order_id: order.order_id,
      user_uuid: order.user_uuid,
      entry_kind: 'manual',
      client_order_id: order.client_order_id,
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity,
      order_type: order.order_type,
      limit_price: order.limit_price,
      status: asBlotterStatusFromOrder(order.status),
      filled_qty: order.filled_qty,
      avg_fill_price: order.avg_fill_price,
      strategy_position_id: null,
      strategy_name: null,
      strategy_config: null,
      created_at: order.created_at,
      updated_at: order.updated_at,
    });
    return order;
  }, [emitBlotterRowUpsert, getToken, userUuid]);

  const scheduleStrategy = useCallback(
    async (request: ScheduleStrategyRequest): Promise<ScheduleStrategyResponse> => {
      if (!userUuid) throw new Error('Missing user id');
      const token = await getToken();
      const scheduled = await tradingService.scheduleStrategy(userUuid, request, token || undefined);
      emitBlotterRowUpsert({
        order_id: scheduled.strategy_position_id,
        user_uuid: userUuid,
        entry_kind: 'strategy',
        client_order_id: null,
        symbol: null,
        side: null,
        quantity: null,
        order_type: null,
        limit_price: null,
        status: scheduled.status,
        filled_qty: null,
        avg_fill_price: null,
        strategy_position_id: scheduled.strategy_position_id,
        strategy_name: scheduled.strategy_name,
        strategy_config: request.config,
        created_at: scheduled.created_at,
        updated_at: scheduled.created_at,
      });
      return scheduled;
    },
    [emitBlotterRowUpsert, getToken, userUuid],
  );

  const fetchMarketOpen = useCallback(async (symbol: string): Promise<boolean> => {
    const normalizedSymbol = normalizeSymbol(symbol);
    if (!normalizedSymbol) throw new Error('Missing symbol');
    const response = await tradingService.getLatestClose('stock', [normalizedSymbol]);
    return response.market_is_open;
  }, []);

  const loadOptionContractsByType = useCallback(
    async (underlying: string, optionType: 'call' | 'put'): Promise<OptionContractLookupRow[]> =>
      tradingService.listOptionContractsByType(underlying, optionType),
    [],
  );

  const loadOptionContractsByStrike = useCallback(
    async (
      underlying: string,
      optionType: 'call' | 'put',
      strike: number,
    ): Promise<OptionContractLookupRow[]> =>
      tradingService.listOptionContractsByStrike(underlying, optionType, strike),
    [],
  );

  const loadOptionContractsByExpiration = useCallback(
    async (
      underlying: string,
      optionType: 'call' | 'put',
      expiration: string,
    ): Promise<OptionContractLookupRow[]> =>
      tradingService.listOptionContractsByExpiration(underlying, optionType, expiration),
    [],
  );

  const value = useMemo<OrderTicketNavigationContextType>(() => ({
    wsConnected,
    ticksBySymbol,
    isSimulationEnabled,
    retainSymbolSubscription,
    releaseSymbolSubscription,
    subscribeToOrderEvents,
    subscribeToPortfolioStateUpdates,
    subscribeToSystemAlerts,
    subscribeToRiskRejects,
    subscribeToIntradaySignalUpdates,
    subscribeToBlotterRowUpserts,
    subscribeToBlotterStatusUpdates,
    startIntradaySignalTest,
    takeOverIntradaySignalTest,
    stopIntradaySignalTest,
    openOrderTicket,
    closeOrderTicket,
    isOrderTicketOpen,
    openStrategyOrderTicket,
    closeStrategyOrderTicket,
    isStrategyOrderTicketOpen,
    previewOrder,
    placeOrder,
    scheduleStrategy,
  }), [
    wsConnected,
    ticksBySymbol,
    isSimulationEnabled,
    retainSymbolSubscription,
    releaseSymbolSubscription,
    subscribeToOrderEvents,
    subscribeToPortfolioStateUpdates,
    subscribeToSystemAlerts,
    subscribeToRiskRejects,
    subscribeToIntradaySignalUpdates,
    subscribeToBlotterRowUpserts,
    subscribeToBlotterStatusUpdates,
    startIntradaySignalTest,
    takeOverIntradaySignalTest,
    stopIntradaySignalTest,
    openOrderTicket,
    closeOrderTicket,
    isOrderTicketOpen,
    openStrategyOrderTicket,
    closeStrategyOrderTicket,
    isStrategyOrderTicketOpen,
    previewOrder,
    placeOrder,
    scheduleStrategy,
  ]);

  return (
    <OrderTicketNavigationContext.Provider value={value}>
      {children}
      <OrderTicketNavigationMenu
        isOpen={isOrderTicketPaneOpen}
        onClose={closeOrderTicket}
        mode={orderTicketPaneMode}
        onModeChange={handlePaneModeChange}
        strategyContext={strategyOrderTicketContext}
        isStrategyModeLocked={isStrategyTicketLocked}
        pathname={location.pathname}
        prefill={orderTicketPrefill}
        ticksBySymbol={ticksBySymbol}
        onSubscribeSymbol={retainSymbolSubscription}
        onUnsubscribeSymbol={releaseSymbolSubscription}
        onPreview={previewOrder}
        onPlaceOrder={placeOrder}
        isSimulationEnabled={isSimulationEnabled}
        onFetchMarketOpen={fetchMarketOpen}
        onLoadOptionContractsByType={loadOptionContractsByType}
        onLoadOptionContractsByStrike={loadOptionContractsByStrike}
        onLoadOptionContractsByExpiration={loadOptionContractsByExpiration}
        disabled={!isAuthenticated || !userUuid}
      />
    </OrderTicketNavigationContext.Provider>
  );
};

export const useOrderTicketNavigation = (): OrderTicketNavigationContextType => {
  const context = useContext(OrderTicketNavigationContext);
  if (!context) {
    throw new Error('useOrderTicketNavigation must be used within OrderTicketNavigationProvider');
  }
  return context;
};

export const useOptionalOrderTicketNavigation = (): OrderTicketNavigationContextType | null => {
  return useContext(OrderTicketNavigationContext) ?? null;
};
